import "./User-login-new.css";
import LoadingSpinner from "../../components/loadingSpinner/LoadingSpinner";
import AccountStatusModal from "../../components/accountStatusModal/AccountStatusModal";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import axios from "axios";
import { useNavigate, Link } from "react-router-dom";
import { LuSun, LuMoon } from "react-icons/lu";
import sourshipLogo from "../../assets/images/sourship-ICON-LOGO.png";
import { clearSession, clearAccountData } from "../../utils/session";
import { noAnswer } from "../../utils/networkOutcome";

// The two terminal account states the backend reports as their own response
// `type` (services/auth/login.php, services/auth/verify_login.php). Everything
// else stays a toast — these two get a modal, because "not approved" and
// "suspended after a refund" are decisions the person needs explained, not
// one-line errors that look like a mistyped password.
const ACCOUNT_NOTICE_TYPES = ["account_rejected", "account_suspended"];

function UserLogin() {
  const navigate = useNavigate();
  const [waitingForLoginVerification, setWaitingForLoginVerification] = useState(true);
  const [accountNotice, setAccountNotice] = useState(null); // { kind, message }

  // Returns true when it consumed the error, so each caller can skip its
  // toast. The payload is a plain string by contract (see the comment in
  // login.php) — rendering it directly is safe.
  const showAccountNoticeIfAny = (error) => {
    const type = error.response?.data?.type;
    if (!ACCOUNT_NOTICE_TYPES.includes(type)) return false;

    setAccountNotice({ kind: type, message: error.response.data.payload });
    return true;
  };

  const [theme, setTheme] = useState(localStorage.getItem("app-theme") || "dark");
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    localStorage.setItem("app-theme", theme);
  }, [theme]);
  const toggleTheme = () => setTheme((prev) => (prev === "dark" ? "light" : "dark"));

  useEffect(() => {
    const verifyToken = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setWaitingForLoginVerification(false);
        return;
      }

      // token verification
      const apiUrl = `${process.env.REACT_APP_BASE_API_URL}/customer/verify_login`;
      const fData = new FormData();
      fData.append("token", token);
      await axios
        .post(apiUrl, fData)
        .then((res) => {
          if (res.data?.type === "success") {
            // verify_login re-issues the token too — the one still held may
            // predate wallet_balance/next_billing_date existing on it (a
            // signup-time token) or an admin's Accept updating billing date.
            const { token: freshToken, ...userFields } = res.data.payload;
            if (freshToken) localStorage.setItem("token", freshToken);
            localStorage.setItem("user", JSON.stringify(userFields));
            if (
              userFields?.approval_status &&
              userFields.approval_status !== "approved"
            ) {
              navigate("/pendingApproval");
            } else {
              navigate("/dashboard");
            }
          } else {
            clearSession();
            setWaitingForLoginVerification(false);
            toast.error("An unknown behavior has occurred");
          }
        })
        .catch((error) => {
          // Only a 401 means the session is over (verify_login answers every
          // signed-out, rejected and suspended case with one). A server error
          // or a dropped connection used to sign the seller out as well; now
          // the token stays, and signing in again or reloading picks it up.
          if (error.response?.status === 401) clearSession();
          // A seller suspended mid-session lands here on their next visit:
          // verify_login 401s with the suspended type, so they get the modal
          // instead of a bare login form with no explanation.
          if (!showAccountNoticeIfAny(error)) {
            const payload = error.response?.data?.payload;
            toast.error(
              noAnswer(error)
                ? "We couldn't reach Sourship. Check your connection and try again."
                : (typeof payload === "string" && payload) || "We couldn't check your session just now. Try again in a moment."
            );
          }
          setWaitingForLoginVerification(false);
        });
    };
    verifyToken();
  }, [navigate]);

  const [waitingForLogin, setWaitingForLogin] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const authenticateUser = async (e) => {
    e.preventDefault();
    if (!userEmail || !userPassword) {
      toast.error("Email and password are required");
      return false;
    }

    setWaitingForLogin(true);
    const apiUrl = `${process.env.REACT_APP_BASE_API_URL}/customer/login`;
    const fData = new FormData();
    fData.append("email", userEmail);
    fData.append("password", userPassword);

    await axios
      .post(apiUrl, fData)
      .then((res) => {
        if (res.data?.type === "success" || res.data?.type === "pending_approval") {
          // No localStorage.clear() here: token and user are overwritten below,
          // and clearing also wiped the seller's theme and sidebar choice.
          // The data cache of whoever was signed in before goes, though.
          clearAccountData();
          localStorage.setItem("token", res.data.payload.token);
          localStorage.setItem("user", JSON.stringify(res.data.payload.user));
          // A Shopify install waiting to be claimed takes the seller back to finish it.
          let shopifyClaim = false;
          try { shopifyClaim = !!localStorage.getItem("shopify-install-claim"); } catch (e) { shopifyClaim = false; }
          navigate(res.data.type === "pending_approval" ? "/pendingApproval" : shopifyClaim ? "/shopifyInstall" : "/dashboard");
        } else {
          clearSession();
          toast.error("Something unexpected happened. Please try again.");
        }
      })
      .catch((error) => {
        if (showAccountNoticeIfAny(error)) {
          clearSession();
          return;
        }
        toast.error(error.response?.data?.payload || error.message);
      });

    setWaitingForLogin(false);
  };

  return waitingForLoginVerification ? (
    <LoadingSpinner />
  ) : (
    <div className="ssi-page">
      {accountNotice && (
        <AccountStatusModal
          kind={accountNotice.kind}
          message={accountNotice.message}
          onClose={() => setAccountNotice(null)}
        />
      )}

      <div className="ssi-glow" aria-hidden="true"></div>
      <div className="ssi-wrap">

        {/* Nav */}
        <div className="ssi-nav">
          <div className="ssi-brand">
            <img src={sourshipLogo} alt="Sourship" className="ssi-brand-mark" />
            <span className="ssi-brand-name">SOURSHIP</span>
          </div>
          <div className="ssi-nav-controls">
            <div className="ssi-tabs">
              <span className="ssi-tab active">Log in</span>
              <Link to="/signUp" className="ssi-tab">Sign up</Link>
            </div>
            <button type="button" className="ssi-theme-btn" onClick={toggleTheme} title="Toggle theme">
              {theme === "dark" ? <LuSun /> : <LuMoon />}
            </button>
          </div>
        </div>

        {/* Main grid */}
        <div className="ssi-grid">

          {/* Left: marketing */}
          <div className="ssi-marketing">
            <div className="ssi-eyebrow-wrap">
              <span className="ssi-eyebrow">
                <span className="ssi-pulse-dot"></span>
                Worldwide fulfillment, simplified
              </span>
            </div>

            <h1 className="ssi-title">
              Cross-border commerce, <span className="ssi-accent">reimagined</span>.
            </h1>

            <p className="ssi-subtitle">
              Source from China, warehouse globally, ship anywhere. Sourship is the
              operating system for modern merchants moving goods across borders.
            </p>

            <div className="ssi-stats">
              <div className="ssi-stat-card">
                <div className="ssi-stat-value">15K+</div>
                <div className="ssi-stat-label">Merchants</div>
              </div>
              <div className="ssi-stat-card">
                <div className="ssi-stat-value">4.2M</div>
                <div className="ssi-stat-label">Parcels / mo</div>
              </div>
              <div className="ssi-stat-card">
                <div className="ssi-stat-value">99.7%</div>
                <div className="ssi-stat-label">On-time</div>
              </div>
            </div>
          </div>

          {/* Right: auth card */}
          <div className="ssi-card">
            <div className="ssi-card-head">
              <div className="ssi-card-title">Welcome back</div>
              <div className="ssi-card-sub">Please enter your details to access your dashboard</div>
            </div>

            <form className="ssi-form" onSubmit={authenticateUser}>
              <label className="ssi-field">
                <span className="ssi-field-label"><i className="far fa-envelope"></i> Email</span>
                <input
                  type="email"
                  placeholder="name@company.com"
                  value={userEmail}
                  onChange={(e) => setUserEmail(e.target.value)}
                  autoComplete="email"
                  name="email"
                  autoFocus
                />
              </label>

              <div className="ssi-pw-field">
                <label className="ssi-field ssi-pw-inner">
                  <span className="ssi-field-label"><i className="fa fa-lock"></i> Password</span>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={userPassword}
                    onChange={(e) => setUserPassword(e.target.value)}
                    autoComplete="current-password"
                    id="passwordField"
                  />
                </label>
                <button
                  type="button"
                  className="ssi-pw-toggle"
                  onClick={() => setShowPassword((v) => !v)}
                  title={showPassword ? "Hide password" : "Show password"}
                >
                  <i className={showPassword ? "fa fa-eye-slash" : "fa fa-eye"}></i>
                </button>
              </div>

              {/* "Remember me" was removed: it changed nothing, a session always
                  lasts the token's 24 hours. */}
              <div className="ssi-form-row" style={{ justifyContent: "flex-end" }}>
                <Link to="/forgotPassword" className="ssi-forgot">Forgot Password?</Link>
              </div>

              <button type="submit" className="ssi-submit-btn" disabled={waitingForLogin}>
                {waitingForLogin ? "Logging in…" : "Log in to dashboard"} →
              </button>
            </form>

            <div className="ssi-footer-question">
              Don't have an account? <Link to="/signUp">Sign up</Link>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}

export default UserLogin;
